import React, { useState, useEffect, Fragment, useCallback, useMemo, useRef } from 'react'
import { request } from '@/utils/axiosUtil'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { FiMenu } from 'react-icons/fi'
import { MdGridView } from 'react-icons/md'
import { RiCloseFill } from 'react-icons/ri'
import { IoClose } from 'react-icons/io5'
import { ChevronDownIcon } from '@heroicons/react/outline'
import { Transition, Dialog } from '@headlessui/react'
import { useDispatch, useSelector } from 'react-redux'
import { getFilters, setFilters } from '@/store/slices/speakersSlice'
import { getCategoriesList } from '@/store/slices/categorySlice'
import { getSpeakerTypesList } from '@/store/slices/speakerTypesSlice'
import { setSpeakersDefaultSeo, setSpeakersSeo } from '@/store/slices/headSlice'
import Button from '@/components/common/Button'
import FaqAndInfo from '@/components/dashboard/FaqAndInfo'
import Loader from '@/components/common/Loader'
import SpeakersView from '@/components/browseSpeakers/SpeakersView'
import SpeakersSearch from '@/components/browseSpeakers/SpeakersSearch'
import SpeakerFilterContainer from '@/components/SpeakerFilter/SpeakerFilterContainer'
import SpeakerType from '@/components/SpeakerFilter/SpeakerType'
import Category from '@/components/SpeakerFilter/Category'
import { useFlags } from 'launchdarkly-react-client-sdk'

const defaultPagination = { limit: 40, skip: 0 }

const defaultSortOrder = {
  id: 0,
  label: 'Exclusive',
  value: 'fields.blackListed,fields.featured,fields.lastName,fields.name',
}

const sortTypes = [
  { id: 2, label: 'Last Name A-Z', value: 'fields.lastName' },
  { id: 3, label: 'Last Name Z-A', value: '-fields.lastName' },
  { id: 4, label: 'First Name A-Z', value: 'fields.firstName' },
  { id: 5, label: 'First Name Z-A', value: '-fields.firstName' },
]

const Speakers = () => {
  const { allowBookmark } = useFlags()

  const router = useRouter()
  const dispatch = useDispatch()

  const filters = useSelector(getFilters)
  const allCategories = useSelector(getCategoriesList)
  const allSpeakerTypes = useSelector(getSpeakerTypesList)

  const [view, setViewing] = useState('Grid')
  const [isLoading, setIsLoading] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [speakerList, setSpeakerList] = useState([])
  const [searchValue, setSearchValue] = useState(router?.query?.query ? router?.query?.query : '')
  const [selectedCategories, setSelectedCategories] = useState([])
  const [selectedCategoriesNames, setSelectedCategoriesNames] = useState([])
  const [selectedType, setSelectedType] = useState('')
  const [selectedSpeakerTypes, setSelectedSpeakerTypes] = useState([])
  const [selectedSpeakerTypeNames, setSelectedSpeakerTypeNames] = useState([])
  const [sortOrder, setSortOrder] = useState(sortTypes?.[0])
  const [pagination, setPagination] = useState(null)
  const [hasMore, setHasMore] = useState(true)
  const [parentCategory, setParentCategory] = useState('')

  const slug = router?.query?.slug ? router?.query?.slug : ''

  const fetchJson = async (apiUrl) => {
    const res = await request({ url: apiUrl })
    if (res?.status == 200) {
      return res?.data
    } else {
      setIsLoading(false)
      router.push(`/${res?.response?.status || res?.status}`)
      return res?.response?.data
    }
  }

  const getMetaTags = async (type, slug) => {
    const response = await fetchJson(
      `${process.env.NEXT_PUBLIC_APIM_BASE_PATH}/metaTags?page=${type}&slug=${slug}`
    )
    if (response?.success) {
      return response?.data
    }
    // let metaData = {}
    // if (type == 'categories') {
    //   const response = await fetchJson(`${process.env.NEXT_PUBLIC_APIM_BASE_PATH}/categories`)
    //   if (response?.success) {
    //     response?.data.forEach((category) => {
    //       if (category.categorySlug == slug) {
    //         metaData = {
    //           pageTitle: category.category,
    //           pageDescription: category.category,
    //           ogImage: null,
    //         }
    //       }
    //     })
    //     return metaData
    //   }
    // } else {
    //   const response = await fetchJson(
    //     `${process.env.NEXT_PUBLIC_APIM_BASE_PATH}/metaTags?page=${type}&slug=${slug}`
    //   )
    //   if (response?.success) {
    //     return response?.data
    //   }
    // }
  }

  const handleSortSpeakers = (order) => {
    setSortOrder(order)
    !isLoading && setIsLoading(true)
    setPagination({ ...defaultPagination })
  }
  const onModelOpenClick = (isOpen) => {}

  const handleFilterSpeakers = (type) => {
    !isLoading && setIsLoading(true)
    setPagination({ ...defaultPagination })
    switch (type) {
      case 'Categories':
        handleCategoryNameChange()
        handleSpeakerTypeNameChange()
        break
      case 'Speaker Type':
        handleSpeakerTypeNameChange()
        handleCategoryNameChange()
        break
      default:
        break
    }
  }

  const init = useCallback(() => {
    router?.asPath?.includes('/speakers') && dispatch(setSpeakersDefaultSeo())
    setIsLoading(true)
    if (slug?.[0]) {
      assignFiltersTypes()
    } else if (filters?.categories || filters?.types || filters?.searchQuery) {
      searchWithCachedFilter(filters)
    } else if (router?.query?.query) {
      setSearchValue(router?.query?.query)
      handleClearAll('')
    } else {
      setPagination({ ...defaultPagination })
    }
  }, [router?.query?.query, slug, filters?.categories, filters?.types, filters?.searchQuery])

  const searchWithCachedFilter = useCallback(
    async (tempFilters = filters) => {
      setSearchValue(tempFilters?.searchQuery || '')
      setSelectedCategories(tempFilters.categories || [])
      setSelectedSpeakerTypes(tempFilters.types || [])
      tempFilters.categories && handleCategoryNameChange(tempFilters.categories)
      tempFilters.types && handleSpeakerTypeNameChange(tempFilters.types)
      setPagination({ ...defaultPagination })
    },
    [filters?.categories, filters?.types, filters?.searchQuery]
  )

  const assignFiltersTypes = useCallback(async () => {
    if (slug) {
      const metaTags = await getMetaTags(slug[0], slug[1])
      const tempFilters = { ...filters }
      dispatch(
        setSpeakersSeo({
          ...metaTags,
          isType:
            slug[0] === 'categories'
              ? 'Category'
              : slug[0].charAt(0).toUpperCase() + slug[0].slice(1),
        })
      )
      let id = ''
      switch (slug[0]) {
        case 'types':
          tempFilters.categories = null
          id = allSpeakerTypes?.find((item) => item?.slug === slug[1])?.id
          id && (tempFilters.types = [id])
          break
        case 'topics':
          tempFilters.types = null
          allCategories.every((item) => {
            id = item?.subCategories?.find((subCategory) => subCategory?.slug === slug[1])?.id || ''
            return !id
          })
          id && (tempFilters.categories = [id])
          break
        case 'categories':
          allCategories?.forEach((item) => {
            if (item?.category === slug[1] || item?.categorySlug == slug[1]) {
              id = item?.subCategories?.map((subCategory) => subCategory?.id || '')
            }
          })
          id && (tempFilters.categories = id)
          onParentCategoryClick(slug[1])
          break
      }
      dispatch(setFilters({ ...tempFilters }))
      searchWithCachedFilter(tempFilters)
    }
  }, [slug, filters])

  const handleProfileRedirect = useCallback(
    (url) => {
      if (selectedCategories || searchValue || selectedSpeakerTypes) {
        dispatch(
          setFilters({
            ...filters,
            categories: selectedCategories.length ? selectedCategories : null,
            types: selectedSpeakerTypes.length ? selectedSpeakerTypes : null,
            searchQuery: searchValue || null,
            relatedSpeakers: makeRelatedSpeakers(url),
            sortOrder: sortOrder,
          })
        )
      }
      router.push(`/speaker/${url}`)
    },
    [selectedCategories, searchValue, selectedSpeakerTypes, speakerList, sortOrder]
  )

  const makeRelatedSpeakers = useCallback(
    (url) => {
      const index = speakerList?.findIndex((x) => x.slug === url)
      let notIArray = []
      const beforeI = speakerList && speakerList?.slice(0, index)
      const afterI = speakerList && speakerList?.slice(index + 1)

      if (beforeI?.length == 0) {
        return afterI?.length >= 4 ? afterI?.slice(0, 4) : afterI
      } else if (afterI?.length == 0) {
        return beforeI?.length >= 4 ? beforeI?.slice(0, 4) : beforeI
      } else if (afterI?.length >= 4) {
        return afterI?.slice(0, 4)
      }

      notIArray = [...afterI, ...beforeI]
      return notIArray?.length >= 4 ? notIArray?.slice(0, 4) : notIArray
    },
    [speakerList]
  )

  const getSpeakersFromApi = useCallback(
    async ({
      _searchValue = searchValue,
      _selectedCategories = selectedCategories,
      _selectedSpeakerTypes = selectedSpeakerTypes,
      _pagination = pagination,
      _sortOrder = sortOrder,
    } = {}) => {
      var pattern = /^[\w&.-a-zñáéíóúüA-Z0-9_ ]+$/
      if (_searchValue && !pattern.test(_searchValue)) {
        setIsLoading(false)
        setSpeakerList([])
        return
      }
      const result = await fetchJson(
        `${process.env.NEXT_PUBLIC_APIM_BASE_PATH}/speakers?query=${encodeURIComponent(
          _searchValue
        )}&categories=${_selectedCategories.toString()}&type=${_selectedSpeakerTypes.toString()}&order=${
          _sortOrder?.value
        }&limit=${_pagination?.limit}&skip=${_pagination?.skip}`
      )
      if (result?.data) {
        result?.data?.length < _pagination?.limit ? setHasMore(false) : !hasMore && setHasMore(true)
        setSpeakerList((prev) =>
          _pagination?.skip > 0 ? [...prev, ...result.data] : [...result.data]
        )
      }
      isLoading && setIsLoading(false)
    },
    [
      searchValue,
      selectedCategories,
      selectedSpeakerTypes,
      speakerList,
      pagination,
      hasMore,
      sortOrder,
      isLoading,
    ]
  )

  const handleClearAll = useCallback(
    (type = selectedType) => {
      switch (type) {
        case 'Categories':
          setSelectedCategories([])
          setSelectedCategoriesNames([])
          break
        case 'Speaker Type':
          setSelectedSpeakerTypes([])
          setSelectedSpeakerTypeNames([])
          break
        default:
          setSelectedType('')
          setSelectedCategories([])
          setSelectedCategoriesNames([])
          setSelectedSpeakerTypes([])
          setSelectedSpeakerTypeNames([])
          dispatch(
            setFilters({
              ...filters,
              categories: null,
              types: null,
              profileCategory: null,
              searchQuery: null,
            })
          )
          handleSortSpeakers(defaultSortOrder)
          break
      }
    },
    [selectedType]
  )

  const onParentCategoryClick = useCallback(
    async (parentCategorySlug) => {
      let id = []
      const obj = {}
      allCategories?.forEach((item) => {
        if (item?.category === parentCategorySlug || item?.categorySlug == parentCategorySlug) {
          obj.category = item?.category
          obj.categorySlug = item?.categorySlug
          id = item?.subCategories?.map((subCategory) => subCategory?.id || '')
        }
      })
      const isContain = id?.every((item) => selectedCategories?.includes(item))
      if (isContain) {
        const result = selectedCategories?.filter((item) => {
          return !id.includes(item)
        })
        id && setSelectedCategories(result)
        if (
          slug[0] == 'categories' &&
          (parentCategorySlug === obj.categorySlug || parentCategorySlug === obj.category)
        ) {
          setParentCategory(parentCategorySlug)
          dispatch(setFilters({ ...filters, categories: result }))
        }
        return
      }
      id && setSelectedCategories([...selectedCategories, ...id])
      dispatch(setFilters({ ...filters, categories: [...selectedCategories, ...id] }))
    },
    [allCategories, selectedCategories, filters, slug]
  )

  const deleteCategories = useCallback(
    (name) => {
      let id = '',
        slugName = ''
      allCategories.every((item) => {
        const obj = item?.subCategories?.find((subCategory) => name === subCategory.name)
        if (obj) {
          id = obj.id
          slugName = obj.slug
        }
        return !id
      })

      if (id) {
        const result = selectedCategories.filter((item) => item !== id)
        const resultNames = selectedCategoriesNames.filter((item) => item !== name)
        const reduxIndex = filters?.categories?.indexOf(id)

        dispatch(
          setFilters({
            ...filters,
            profileCategory: filters?.profileCategory === id ? null : filters?.profileCategory,
            categories: reduxIndex > -1 && result.length ? result : null,
            searchQuery: searchValue || null,
          })
        )
        setSelectedCategories(result)
        setSelectedCategoriesNames(resultNames)
        !isLoading && setIsLoading(true)
        setPagination({ ...defaultPagination })

        if (slug[1] === name || slug[1] === slugName || slug[0] === 'categories') {
          router.push(
            {
              pathname: '/speakers',
            },
            undefined,
            { shallow: true }
          )
        }
      }
    },
    [
      allCategories,
      selectedCategories,
      selectedCategoriesNames,
      filters,
      searchValue,
      selectedSpeakerTypes,
      slug,
      getSpeakersFromApi,
      isLoading,
    ]
  )

  const deleteSpeakerTypes = useCallback(
    (name) => {
      const { id, slug: slugName } = allSpeakerTypes?.find((item) => name === item.name) || {
        id: '',
        slugName: '',
      }
      if (id) {
        const result = selectedSpeakerTypes.filter((item) => item !== id)
        const resultNames = selectedSpeakerTypeNames.filter((item) => item !== name)
        const reduxId = filters?.types?.indexOf(id)

        dispatch(
          setFilters({
            ...filters,
            types: reduxId > -1 && result.length ? result : null,
            searchQuery: searchValue || null,
          })
        )

        setSelectedSpeakerTypes(result)
        setSelectedSpeakerTypeNames(resultNames)
        !isLoading && setIsLoading(true)
        setPagination({ ...defaultPagination })

        if (slug[1] === name || slug[1] === slugName) {
          router.push(
            {
              pathname: '/speakers',
            },
            undefined,
            { shallow: true }
          )
        }
      }
    },
    [
      allSpeakerTypes,
      selectedSpeakerTypes,
      selectedSpeakerTypeNames,
      filters,
      slug,
      getSpeakersFromApi,
      searchValue,
      selectedCategories,
      isLoading,
    ]
  )

  const handleCategoryNameChange = useCallback(
    (tempCategories = selectedCategories) => {
      const categoriesResults = []
      allCategories?.forEach((item) =>
        item?.subCategories?.forEach((subCategory) => {
          tempCategories.includes(subCategory.id) && categoriesResults.push(subCategory.name)
        })
      )
      setSelectedCategoriesNames(categoriesResults)
      if (slug[0] == 'categories' && parentCategory !== '') {
        router.push(
          {
            pathname: '/speakers',
          },
          undefined,
          { shallow: true }
        )
        setParentCategory('')
      }
    },
    [allCategories, selectedCategories]
  )

  const handleSpeakerTypeNameChange = useCallback(
    (tempSpeakerTypes = selectedSpeakerTypes) => {
      const speakerTypesResults = []
      allSpeakerTypes?.forEach((item) => {
        tempSpeakerTypes.includes(item.id) ? speakerTypesResults.push(item.name) : []
      })
      setSelectedSpeakerTypeNames(speakerTypesResults)
    },
    [allSpeakerTypes, selectedSpeakerTypes]
  )
  const showOverflow = () => {
    const scrollPosition = window.pageYOffset
    document.querySelector('body').style.removeProperty('overflow')
    document.querySelector('body').style.removeProperty('position')
    document.querySelector('body').style.removeProperty('top')
    document.querySelector('body').style.removeProperty('width')
    window.scrollTo(0, scrollPosition)
  }

  const hidden = () => {
    const scrollPosition = window.pageYOffset
    document.querySelector('body').style.overflow = 'hidden'
    document.querySelector('body').style.position = 'fixed'
    document.querySelector('body').style.top = `-${scrollPosition}px`
    document.querySelector('body').style.width = '100%'
  }

  const renderSelectedType = useMemo(() => {
    switch (selectedType) {
      case 'Categories':
        return (
          <>
            <div className="relative">
              <Category
                isMobileOpen={true}
                categories={allCategories}
                selectedCategories={selectedCategories}
                handleCategoryChange={(selectedData) => setSelectedCategories(selectedData)}
                handleFilterSpeakers={() => handleFilterSpeakers(selectedType)}
              />
            </div>
          </>
        )
      case 'Speaker Type':
        return (
          <>
            <div className="relative">
              <SpeakerType
                isMobileOpen={true}
                speakerTypes={allSpeakerTypes}
                selectedSpeakerTypes={selectedSpeakerTypes}
                handleSpeakerTypeChange={(selectedTypes) => setSelectedSpeakerTypes(selectedTypes)}
                handleFilterSpeakers={() => handleFilterSpeakers(selectedType)}
              />
            </div>
          </>
        )
      default:
        return (
          <div className="px-4">
            <div className="px-3 mt-3">
              <h1 className="tagline-font">Sort Options</h1>
              <div className="mt-4">
                {sortTypes?.map((sort) => (
                  <span className="flex mb-3" key={`sort ${sort?.label}`}>
                    <span onClick={() => handleSortSpeakers(sort)} value={sort?.label} name="sort">
                      <Image
                        src={`/static/icons/radio-${
                          sortOrder?.id === sort?.id ? 'checked' : 'uncheck'
                        }.svg`}
                        alt="sort"
                        height={20}
                        width={20}
                      />
                    </span>
                    <label className="ml-3">{sort?.label}</label>
                  </span>
                ))}
              </div>
            </div>
            <div className="w-full border-t border-gray-400" />
            <div className="py-5">
              <span
                className="flex justify-between px-4"
                onClick={() => setSelectedType('Categories')}
              >
                <h1 className="tagline-font">Categories & Topics</h1>
                <ChevronDownIcon className="w-5 h-5 transform -rotate-90" />
              </span>
            </div>
            <div className="w-full border-t border-gray-400" />
            <div className="px-4 py-5">
              <span
                className="flex justify-between"
                onClick={() => setSelectedType('Speaker Type')}
              >
                <h1 className="tagline-font">Speaker Types</h1>
                <ChevronDownIcon className="w-5 h-5 transform -rotate-90" />
              </span>
            </div>
          </div>
        )
    }
  }, [
    allCategories,
    allSpeakerTypes,
    selectedCategories,
    selectedSpeakerTypes,
    sortOrder,
    selectedType,
  ])

  const renderSelectedOptions = useMemo(
    () => (
      <>
        {selectedCategoriesNames.map((item, i) => (
          <div key={i}>
            <span className="category-tag">
              <p>{item}</p>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <IoClose
                role="button"
                aria-label="close"
                className="custom-close"
                onClick={() => deleteCategories(item)}
              />
            </span>
          </div>
        ))}
        {selectedSpeakerTypeNames.map((item, i) => (
          <div key={i}>
            <span className="category-tag">
              <p>{item}</p>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <IoClose
                role="button"
                aria-label="close"
                className="custom-close"
                size={20}
                onClick={() => deleteSpeakerTypes(item)}
              />
            </span>
          </div>
        ))}
        {searchValue && (
          <div>
            <span className="category-tag">
              <p>{searchValue}</p>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <IoClose
                role="button"
                aria-label="close"
                className="custom-close"
                size={20}
                onClick={() => {
                  setSearchValue('')
                  !isLoading && setIsLoading(true)
                  setPagination({ ...defaultPagination })
                }}
              />
            </span>
          </div>
        )}
      </>
    ),
    [selectedCategoriesNames, selectedSpeakerTypeNames, searchValue, isLoading]
  )

  useEffect(() => {
    if (router?.isReady) {
      init()
    }
  }, [router?.isReady, router?.query?.query, router?.query?.slug])

  useEffect(() => {
    if (pagination) {
      getSpeakersFromApi({ _pagination: pagination })
    }
  }, [pagination])

  const divRef = useRef()

  useEffect(() => {
    const handleClickOutside = (event) => {
      showOverflow()
      if (divRef?.current && !divRef.current.contains(event.target)) {
        if (
          selectedCategories?.length !== selectedCategoriesNames?.length ||
          selectedSpeakerTypes?.length !== selectedSpeakerTypeNames?.length
        ) {
          handleFilterSpeakers && handleFilterSpeakers(selectedType)
          setIsModalOpen(false)
          onModelOpenClick(false)
        }
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [handleFilterSpeakers])

  const autoFilterOnSelect = useCallback(() => {
    if (
      selectedCategories?.length !== selectedCategoriesNames?.length ||
      selectedSpeakerTypes?.length !== selectedSpeakerTypeNames?.length
    ) {
      handleFilterSpeakers && handleFilterSpeakers(selectedType)
    }
  }, [selectedCategories, selectedCategoriesNames, selectedSpeakerTypes, selectedSpeakerTypeNames])

  if (isLoading) {
    return <Loader />
  }

  return (
    <div className="-mt-1 border-hidden">
      <main className="bg-black">
        <div className="uta-container">
          <div className="flex-wrap sm:m-auto md:m-0">
            <div className="flex justify-between m-auto mt-10 mb-2">
              <h1 className="object-fill text-2xl leading-7 tracking-wider text-white md:text-4xl tagline-font md:leading-10">
                YOUR TOP RESULTS
              </h1>
            </div>

            <SpeakersSearch
              searchValue={searchValue}
              setSearchValue={(value) => {
                setSearchValue(value)
                !isLoading && setIsLoading(true)
                setPagination({ ...defaultPagination })
              }}
            />

            <div className="grid w-full grid-cols-1 border md:hidden">
              <div
                className="flex justify-between px-4 py-3 bg-black"
                onClick={() => {
                  setIsModalOpen(true)
                  onModelOpenClick(true)
                  hidden()
                }}
              >
                <button type="button" className="tracking-wider tagline-font">
                  Filter
                </button>
                <ChevronDownIcon className="w-5 h-5 ml-2 text-white" />
              </div>

              <Transition appear show={isModalOpen} as={Fragment} className="md:hidden">
                <Dialog
                  as="div"
                  className="relative z-[3000000000]"
                  onClose={() => {
                    setIsModalOpen(false)
                    onModelOpenClick(false)
                  }}
                >
                  <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <div className="fixed inset-0 bg-black bg-opacity-80" />
                  </Transition.Child>
                  <div className="fixed inset-0 top-5" ref={divRef}>
                    <div className="flex justify-center py-4 text-center bg-black h-screen">
                      <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                      >
                        <Dialog.Panel className="left-0 right-0 w-full text-left transition-all transform bg-black h-[82vh]">
                          <Dialog.Title as="h3">
                            <div className={'grid grid-cols-6 px-4'}>
                              <span
                                className={selectedType ? 'flex pt-4' : 'hidden '}
                                onClick={() => {
                                  autoFilterOnSelect()
                                  setSelectedType('')
                                }}
                              >
                                <ChevronDownIcon className="w-5 h-5 transform rotate-90" />
                                <h5 className="regular-font text-sm mt-0.5">BACK</h5>
                              </span>

                              <h1
                                className={`${
                                  selectedType
                                    ? 'py-4  uppercase tagline-font text-sm col-start-3 col-span-3'
                                    : 'tagline-font tracking-wide py-4 text-center text-sm col-start-2 col-span-4'
                                }`}
                              >
                                {(selectedType == 'Speaker Type' && 'Speaker Types') ||
                                  'FILTER SPEAKERS'}
                              </h1>

                              <RiCloseFill
                                className="relative w-6 h-6 col-end-7 text-white cursor-pointer -right-6 top-3 "
                                onClick={() => {
                                  autoFilterOnSelect()
                                  setIsModalOpen(false)
                                  onModelOpenClick(false)
                                  showOverflow()
                                }}
                              />
                            </div>
                          </Dialog.Title>
                          <div className="w-full px-4 border-t border-gray-400"></div>
                          <Dialog.Description className="overflow-auto h-[60vh] relative bottom-24 top-3">
                            {renderSelectedType}
                          </Dialog.Description>
                          <>
                            <div className="fixed bottom-0 left-0 right-0 w-full bg-black border-t border-gray-400">
                              <div className="flex items-center h-[100px] px-4">
                                <div className="grid w-full grid-cols-2 gap-3">
                                  <Button
                                    className="py-3 border tagline-font"
                                    label="Clear All"
                                    handleClick={handleClearAll}
                                  />
                                  <Button
                                    className="py-3 text-black bg-white border tagline-font"
                                    label="Apply"
                                    handleClick={() => {
                                      handleFilterSpeakers(selectedType)
                                      setIsModalOpen(false)
                                      onModelOpenClick(false)
                                      showOverflow()
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          </>
                        </Dialog.Panel>
                      </Transition.Child>
                    </div>
                  </div>
                </Dialog>
              </Transition>
            </div>
            <SpeakerFilterContainer
              categories={allCategories}
              speakerTypes={allSpeakerTypes}
              selectedCategories={selectedCategories}
              selectedCategoriesNames={selectedCategoriesNames}
              handleCategoryChange={(selectedData) => setSelectedCategories(selectedData)}
              getSpeakersFromApi={getSpeakersFromApi}
              selectedSpeakerTypes={selectedSpeakerTypes}
              selectedSpeakerTypeNames={selectedSpeakerTypeNames}
              handleSpeakerTypeChange={(selectedTypes) => setSelectedSpeakerTypes(selectedTypes)}
              handleSortSpeakers={handleSortSpeakers}
              sortOrder={sortOrder}
              onParentCategoryClick={onParentCategoryClick}
              sortTypes={sortTypes}
              handleFilterSpeakers={handleFilterSpeakers}
            />

            {speakerList?.length === 0 ? (
              <div className="flex justify-between mt-6 md:mb-3">
                <div className="w-full lg:flex lg:items-start">
                  <div className="hidden md:flex md:flex-wrap lg:mt-1">{renderSelectedOptions}</div>
                </div>
                <div className="z-0 flex gap-4">
                  <div
                    role="button"
                    aria-label="grid view"
                    className={`${
                      view === 'Grid'
                        ? 'flex-1 flex-start z-0 mt-1'
                        : 'cursor-pointer flex-1 flex-start z-0 mt-1'
                    }`}
                    onClick={() => setViewing('Grid')}
                  >
                    <MdGridView
                      className={`w-7 h-7 z-0 -mt-0.5 ${
                        view === 'Grid' ? 'text-white' : 'text-custom-gray-80'
                      }`}
                    />
                    <h4
                      className={`mt-1 text-xs text-center ${
                        view === 'Grid' ? 'text-white' : 'text-custom-gray-80'
                      }`}
                      role="presentation"
                    >
                      Grid
                    </h4>
                  </div>
                  <div className="mt-1 border-r border-gray-500 h-11"></div>
                  <div
                    role="button"
                    aria-label="list view"
                    className={`${
                      view === 'List'
                        ? 'flex-1 row-end-4 z-0'
                        : 'cursor-pointer flex-1 row-end-4 z-0'
                    }`}
                    onClick={() => setViewing('List')}
                  >
                    <FiMenu
                      className={`z-0 -mt-1 w-7 h-9 ${
                        view === 'List' ? 'text-white' : 'text-custom-gray-80'
                      }`}
                    />
                    <h4
                      className={`text-center text-xs mt-[3px] ${
                        view === 'List' ? 'text-white' : 'text-custom-gray-80'
                      }`}
                      role="presentation"
                    >
                      List
                    </h4>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex justify-between mt-6 mb-3">
                <div className="lg:flex lg:items-start">
                  <div className="hidden md:w-full md:flex md:flex-wrap lg:mt-1">
                    {renderSelectedOptions}
                  </div>
                </div>
                <div className="z-0 flex gap-4">
                  <div
                    role="button"
                    aria-label="grid view"
                    className={`${
                      view === 'Grid'
                        ? 'flex-1 flex-start z-0 mt-1'
                        : 'cursor-pointer flex-1 flex-start z-0 mt-1'
                    }`}
                    onClick={() => setViewing('Grid')}
                  >
                    <MdGridView
                      className={`w-7 h-7 z-0 -mt-[1px] ${
                        view === 'Grid' ? 'text-white' : 'text-custom-gray-80'
                      }`}
                    />
                    <h4
                      className={`mt-1 text-xs text-center ${
                        view === 'Grid' ? 'text-white' : 'text-custom-gray-80'
                      }`}
                      role="presentation"
                    >
                      Grid
                    </h4>
                  </div>
                  <div className="mt-1 border-r border-gray-500 h-11"></div>
                  <div
                    role="button"
                    aria-label="list view"
                    className={`${
                      view === 'List'
                        ? 'flex-1 row-end-4 z-0'
                        : 'cursor-pointer flex-1 row-end-4 z-0'
                    }`}
                    onClick={() => setViewing('List')}
                  >
                    <FiMenu
                      className={`z-0 -mt-1 w-7 h-9 ${
                        view === 'List' ? 'text-white' : 'text-custom-gray-80'
                      }`}
                    />
                    <h4
                      className={`text-xs text-center mt-[3px] ${
                        view === 'List' ? 'text-white' : 'text-custom-gray-80'
                      }`}
                      role="presentation"
                    >
                      List
                    </h4>
                  </div>
                </div>
              </div>
            )}
            <div className="flex flex-wrap w-full mr-2 md:hidden">{renderSelectedOptions}</div>
            <div className="flex flex-wrap lg:w-full md:w-full md:hidden"></div>
            {speakerList?.length === 0 ? (
              <div className="flex justify-center pb-96 pt-28">
                <h4 className="text-sm lg:text-xl md:text-xl xs:text-lg text-[#808080]">
                  No speakers match your search criteria
                </h4>
              </div>
            ) : (
              <SpeakersView
                allowBookmark={allowBookmark}
                setIsLoading={setIsLoading}
                speakerList={speakerList}
                view={view}
                handleProfileRedirect={handleProfileRedirect}
                loadMoreRows={() =>
                  setPagination((prev) => ({ ...prev, skip: prev?.skip + prev?.limit }))
                }
                hasMore={hasMore}
              />
            )}
          </div>
        </div>
      </main>
      <FaqAndInfo />
    </div>
  )
}

export default Speakers
